import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { errorMessage } from '../lib/api';
import { firebaseConfigured } from '../lib/env';
import { getGuestAuth } from '../lib/firebase';

/**
 * The anonymous session behind /r/:token.
 *
 * slate-api treats a caller as a `requestLink` actor only when the ID token is
 * anonymous *and* `X-Slate-Request-Token` names a live link. This mints that
 * anonymous token in the guest Firebase app, which is deliberately separate
 * from the signed-in app so that a Slate user who opens somebody else's
 * request link stays signed in to their own account.
 */

export interface GuestSession {
  ready: boolean;
  error: string | null;
}

export function useGuestSession(enabled: boolean): GuestSession {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!firebaseConfigured) {
      setError('This Slate deployment is missing its Firebase configuration.');
      return;
    }

    let cancelled = false;
    const guestAuth = getGuestAuth();

    const unsubscribe = onAuthStateChanged(guestAuth, (user) => {
      if (cancelled) return;
      if (user?.isAnonymous) {
        setReady(true);
        return;
      }
      signInAnonymously(guestAuth).catch((cause: unknown) => {
        if (cancelled) return;
        setError(errorMessage(cause));
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled]);

  return { ready, error };
}
